'use strict';

Object.defineProperty(exports, "__esModule", {
    value: true
});
exports.getDateSeparators = getDateSeparators;
exports.getDOBSeparator = getDOBSeparator;
exports.getFormattedDob = getFormattedDob;
exports.isOfLegalAge = isOfLegalAge;
exports.validateDob = validateDob;
exports.parseDob = parseDob;
exports.isDateStringValid = isDateStringValid;
exports.getDateFormat = getDateFormat;
exports.isValidDate = isValidDate;
exports.reset = reset;
var dobSeparators = ['-', '.', '/'];
// Cache values for current locale
// Must be unset on init
var dobSeparator = void 0;
var dateFormat = void 0;

/**
 * All supported date separators
 */
function getDateSeparators() {
    return dobSeparators;
}

/**
 * Returns date separator for the specified format
 * Eg: Returns "-" for DD-MM-YYYY, "/" dor DD/MM/YYYY
 * @param  {String} dobFormat Date format as specified by griffin
 * @return {String}           Date separator
 */
function getDOBSeparator(dobFormat) {
    // If we have cached the dobSeparator, return it
    if (dobSeparator) {
        return dobSeparator;
    }

    var separator = '';
    getDateSeparators().forEach(function (ele, iter) {
        if (dobFormat.indexOf(ele) !== -1) {
            // eslint-disable-line consumerweb/no-indexof
            separator = ele;
        }
    });
    if (separator) {
        dobSeparator = separator;
    }
    return separator;
}

/**
 * Formats the date based on the specified pattern
 * @param  {String} dob        DOB to format
 * @param  {String} dobPattern Date Pattern as specified by griffin
 * @return {String}            Formatted DOB
 */
function getFormattedDob(dob, dobPattern) {
    var dobFormat = getDateFormat(dobPattern);
    var dateSeparator = getDOBSeparator(dobPattern);
    var dateDigitsOnly = dob.replace(/\D+/g, '').slice(0, 8);
    var formattedDate = '';

    // Did user manually enter the separator?
    var lastCharIsSeparator = false;
    if (dob[dob.length - 1] === dateSeparator) {
        lastCharIsSeparator = true;
    }

    if (dobFormat === 'DMY' || dobFormat === 'MDY') {
        // If '/' is the dateSeparator,
        // Format as [2digits]/[2digits]/[4digits]
        // Eg: DD/MM/YYYY or MM/DD/YYYY

        var dd = dateDigitsOnly.slice(0, 2);
        var mm = dateDigitsOnly.slice(2, 4);
        var yy = dateDigitsOnly.slice(4);

        // Theres probably a better way to do this
        if (dd) {
            formattedDate = dd;
            if (dateDigitsOnly.length > 2 || dateDigitsOnly.length === 2 && lastCharIsSeparator) {
                formattedDate += dateSeparator;
            }
        }
        if (mm) {
            formattedDate += mm;
            if (dateDigitsOnly.length > 4 || dateDigitsOnly.length === 4 && lastCharIsSeparator) {
                formattedDate += dateSeparator;
            }
        }
        if (yy) {
            formattedDate += yy;
        }
    } else {
        // Format as YYYY/DD/MM
        var _yy = dateDigitsOnly.slice(0, 4);
        var _mm = dateDigitsOnly.slice(4, 6);
        var _dd = dateDigitsOnly.slice(6);
        if (_yy) {
            formattedDate = _yy;
            if (dateDigitsOnly.length > 4 || dateDigitsOnly.length === 4 && lastCharIsSeparator) {
                formattedDate += dateSeparator;
            }
        }
        if (_mm) {
            formattedDate += _mm;
            if (dateDigitsOnly.length > 6 || dateDigitsOnly.length === 6 && lastCharIsSeparator) {
                formattedDate += dateSeparator;
            }
        }
        if (_dd) {
            formattedDate += _dd;
        }
    }

    return formattedDate;
}

function isOfLegalAge(dob, dobPattern) {
    var dateObject = parseDob(dob, dobPattern);
    if (isDateStringValid(dateObject)) {
        var year = dateObject.year,
            month = dateObject.month,
            day = dateObject.day;

        var birthDate = new Date(year, month - 1, day);
        // user should be of exact 18 years of age or older
        var tempDate = new Date(birthDate.getFullYear() + 18, birthDate.getMonth(), birthDate.getDate());

        return tempDate <= new Date().setHours(0, 0, 0, 0);
    }

    return false;
}

/**
 * Validates date of birth from DoB string.
 *
 * @param {string} date of birth
 * @return {boolean} is valid
 */
function validateDob(dob, dobPattern) {
    var dateObject = parseDob(dob, dobPattern);
    if (isDateStringValid(dateObject)) {
        var year = dateObject.year,
            month = dateObject.month,
            day = dateObject.day;

        return isDateValid(year, month - 1, day);
    }

    return false;
}

function isDateValid(year, month, day) {
    // dates like 02/31 33/33 should return false
    var date = new Date(year, month, day);

    var fullYear = date.getFullYear();
    var dateMonth = date.getMonth();
    var dateDay = date.getDate();

    // If birth year is in the future or before 1900, dob is invalid
    var now = new Date();
    if (fullYear > now.getFullYear() || fullYear < 1900) {
        return false;
    }

    if (fullYear === parseInt(year, 10) && dateMonth === parseInt(month, 10) && dateDay === parseInt(day, 10)) {
        return true;
    }
    return false;
}

/**
 * Parses the date string to return an object in the following format
 * {
 * 	day,
 * 	month,
 * 	year
 * }
 * Eg: If input is "11/12/2017", returns
 * {
 * 	day: 11,
 * 	month: 12,
 * 	year: 2017
 * }
 */
function parseDob(inputVal, dobPattern) {
    var day = void 0;
    var month = void 0;
    var year = void 0;
    var sep = getDOBSeparator(dobPattern);
    var partsArr = inputVal.split(sep);
    var first = partsArr[0];
    var sec = partsArr[1];
    var third = partsArr[2];

    if (getDateFormat(dobPattern) === 'DMY') {
        day = first;
        month = sec;
        year = third;
    } else if (getDateFormat(dobPattern) === 'YMD') {
        year = first;
        month = sec;
        day = third;
    } else {
        month = first;
        day = sec;
        year = third;
    }

    return {
        day: day,
        month: month,
        year: year
    };
}

/**
 * Checks whether the date string is a valid date
 */
function isDateStringValid(_ref) {
    var day = _ref.day,
        month = _ref.month,
        year = _ref.year;

    // Validate only on entering the last digit of the dob
    var dayLen = ('' + day).length;
    var monLen = ('' + month).length;
    var yrLen = ('' + year).length;
    var validDay = dayLen === 1 || dayLen === 2;
    var validMon = monLen === 1 || monLen === 2;
    var validYr = yrLen === 4;
    return validDay && validMon && validYr;
}

/**
 * Returns the date format as a 3 letter string
 * Returns
 * 	1. 'DMY' for DDMMYYYY format
 * 	2. 'MDY' for MMDDYYYY format
 * 	3. 'YMD' for YYYYMMDD format
 * @param  {String} dobFormat Format of Date of Birth
 * @return {String}           Date Format
 */
function getDateFormat(dobFormat) {
    // cache date format value
    if (dateFormat) {
        return dateFormat;
    }

    var dateFormatComponents = dobFormat.split(getDOBSeparator(dobFormat));

    // Its can be 'D' or 'DD'
    var dIdx = dateFormatComponents.indexOf('D') !== -1 ? dateFormatComponents.indexOf('D') : dateFormatComponents.indexOf('DD'),
        // eslint-disable-line consumerweb/no-indexof
    mIdx = dateFormatComponents.indexOf('MM'); // eslint-disable-line consumerweb/no-indexof

    var formatComponentMap = getFormatComponentIdxMappings();
    if (formatComponentMap.DMY.day === dIdx && formatComponentMap.DMY.month === mIdx) {
        dateFormat = 'DMY';
        return dateFormat;
    } else if (formatComponentMap.MDY.day === dIdx && formatComponentMap.MDY.month === mIdx) {
        dateFormat = 'MDY';
        return dateFormat;
    }

    // Neither DMY nor MDY
    dateFormat = 'YMD';
    return dateFormat;
}

function getFormatComponentIdxMappings() {
    return {
        DMY: {
            day: 0,
            month: 1,
            year: 2
        },
        MDY: {
            day: 1,
            month: 0,
            year: 2
        },
        YMD: {
            day: 2,
            month: 1,
            year: 0
        }
    };
}

function isValidDate(year, month, day) {
    // dates like 02/31 33/33 should return false
    var date = new Date(year, month, day);

    var fullYear = date.getFullYear();
    var dateMonth = date.getMonth();
    var dateDay = date.getDate();

    // If birth year is in the future or before 1900, dob is invalid
    var now = new Date();
    if (fullYear > now.getFullYear() || fullYear < 1900) {
        return false;
    }

    if (fullYear === parseInt(year, 10) && dateMonth === parseInt(month, 10) && dateDay === parseInt(day, 10)) {
        return true;
    }
    return false;
}

/**
 * Resets the cached variables. Required mostly for unit tests
 */
function reset() {
    dobSeparator = null;
    dateFormat = null;
}


//////////////////
// WEBPACK FOOTER
// /donatenodeweb-ci-2793/workspace/donate-gro-release/~/guest-payment/dist/util/dateUtils.js
// module id = 458
// module chunks = 5